import { EnvelopeSimple } from '@phosphor-icons/react';
import { authMessage } from '@shh/shared';
import { Button, PasswordField, Sheet, Text, TextField } from '@shh/ui';
import { useState } from 'react';
import { friendlyError, requestEmailChange } from '../data/api';
import { accountText as t } from '../i18n/account';
import { firebase } from '../lib/firebase';
import { locale } from '../lib/locale';
import { startText } from '../i18n/start';

/** Only e-mail + password accounts can change their address – Google manages its own. */
export const hasPassword = () =>
  Boolean(firebase().auth.currentUser?.providerData.some((p) => p.providerId === 'password'));

/**
 * "E-Mail ändern" (1.17): new address + password, then a confirmation link goes to the new
 * address. Nothing changes until it is opened (AccountActionPage, mode verifyAndChangeEmail).
 */
export function ChangeEmailButton() {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState<string>();
  const [error, setError] = useState<string>();

  const send = async () => {
    setBusy(true);
    setError(undefined);
    try {
      await requestEmailChange(email.trim(), password);
      setSent(email.trim());
    } catch (e) {
      const code = (e as { code?: unknown }).code;
      setError(
        typeof code === 'string' && code.startsWith('auth/')
          ? /invalid-credential|wrong-password|invalid-login/.test(code)
            ? startText.wrongPassword
            : authMessage(code, locale)
          : friendlyError(e),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button
        variant="quiet"
        icon={EnvelopeSimple}
        className="self-start"
        onPress={() => setOpen(true)}
      >
        {t.changeEntry}
      </Button>
      <Sheet
        isOpen={open}
        onOpenChange={(o) => {
          if (busy) return;
          setOpen(o);
          setError(undefined);
          setPassword('');
          if (!o) {
            setSent(undefined);
            setEmail('');
          }
        }}
        title={t.changeTitle}
      >
        {sent ? (
          <p
            role="status"
            className="rounded-control bg-feedback-success-surface p-inset-sm font-semibold"
          >
            {t.changeSent(sent)}
          </p>
        ) : (
          <form
            className="flex flex-col gap-gap-md"
            onSubmit={(e) => {
              e.preventDefault();
              void send();
            }}
          >
            <Text>{t.changeIntro}</Text>
            <TextField
              label={t.newEmail}
              type="email"
              autoComplete="email"
              value={email}
              onChange={setEmail}
              isRequired
            />
            <PasswordField
              label={t.password}
              autoComplete="current-password"
              value={password}
              onChange={setPassword}
              isRequired
            />
            {error && (
              <p role="alert" className="font-semibold text-feedback-danger">
                {error}
              </p>
            )}
            <div className="flex flex-wrap gap-gap-sm">
              <Button
                type="submit"
                icon={EnvelopeSimple}
                isDisabled={busy || !email.trim() || !password}
              >
                {t.changeSend}
              </Button>
              <Button variant="quiet" isDisabled={busy} onPress={() => setOpen(false)}>
                {t.cancel}
              </Button>
            </div>
          </form>
        )}
      </Sheet>
    </>
  );
}
